import baseEntries from './base.json' with { type: 'json' };
import type {
  NoticeArticle,
  NoticeLocale,
  NoticeOverrides,
  NoticeEntry,
  NoticeTranslations,
} from './types';
export type {
  NoticeArticle,
  NoticeLocale,
  NoticeOverrides,
  NoticeEntry,
  NoticeTranslations,
} from './types';

const SUPPORTED_LOCALES: NoticeLocale[] = ['ko', 'en'];
const BASE_ENTRIES = baseEntries as NoticeEntry[];

function buildBaseArticles(locale: NoticeLocale): NoticeArticle[] {
  return BASE_ENTRIES.flatMap(entry => {
    const localized = resolveTranslation(entry.translations, locale);
    if (!localized) {
      return [];
    }

    return [
      {
        id: entry.id,
        isImportant: entry.isImportant,
        date: entry.date,
        title: localized.title,
        content: localized.content,
      },
    ];
  });
}

function resolveTranslation(
  translations: NoticeTranslations,
  locale: NoticeLocale,
) {
  return translations[locale] ?? translations.ko ?? translations.en ?? null;
}

function mergeArticles(
  baseArticles: NoticeArticle[],
  overrides: NoticeArticle[] = [],
) {
  const map = new Map<number, NoticeArticle>();

  for (const article of baseArticles) {
    map.set(article.id, article);
  }

  for (const override of overrides) {
    const existing = map.get(override.id);
    if (existing) {
      map.set(override.id, {
        ...existing,
        ...override,
      });
    } else {
      map.set(override.id, override);
    }
  }

  // Sort by isImportant first, then by date (newest first)
  return Array.from(map.values()).sort((a, b) => {
    // Important notices come first
    if (a.isImportant !== b.isImportant) {
      return a.isImportant ? -1 : 1;
    }
    // Within same importance, sort by date (newest first)
    const dateA = a.date ?? '';
    const dateB = b.date ?? '';
    if (dateA !== dateB) {
      return dateB.localeCompare(dateA);
    }
    return b.id - a.id;
  });
}

const BASE_DATA = SUPPORTED_LOCALES.reduce<Record<NoticeLocale, NoticeArticle[]>>(
  (acc, locale) => {
    acc[locale] = buildBaseArticles(locale);
    return acc;
  },
  {} as Record<NoticeLocale, NoticeArticle[]>,
);

export function getBaseNotice(): Record<NoticeLocale, NoticeArticle[]> {
  return {
    ko: [...BASE_DATA.ko],
    en: [...BASE_DATA.en],
  };
}

export function getNoticeData(
  overrides: NoticeOverrides = {},
): Record<NoticeLocale, NoticeArticle[]> {
  return SUPPORTED_LOCALES.reduce<Record<NoticeLocale, NoticeArticle[]>>(
    (acc, locale) => {
      const baseArticles = BASE_DATA[locale] ?? [];
      const overrideArticles = overrides[locale] ?? [];
      acc[locale] = mergeArticles(baseArticles, overrideArticles);
      return acc;
    },
    {} as Record<NoticeLocale, NoticeArticle[]>,
  );
}
